import React, { FC, useState, useEffect, useRef, useMemo } from 'react'
import Alert from '@mui/material/Alert'
import { Flex, Spin } from 'antd'
import { selectCurrentZone } from '../../store/global'
import NavigationHeader from 'components/NavigationHeader/NavigationHeader'
import Search from 'UI/Search/Search'
import { CurrentZone } from 'utils/types'
import { useCDTFAOverviewQuery } from './useCDTFAOverviewQuery'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import cx from 'classnames'
import messages from './messages'
import { CDTFAEntityForTable } from 'containers/CDTFATablePage/types'
import CDTFATablePageOverview from '../../components/cdtfa/CDTFATablePageOverview'
import { Filter, FilterAdditionalData } from 'components/FilterDrawer/FilterDrawer.config'
import useVerticalSearchAndSort from 'hooks/useVerticalSearchAndSort'
import { useFilterOptionsQuery } from 'hooks/useFilterOptionsQuery'
import { filtersInitialState } from './CDTFATablePage.config'
import { useCDTFAMainTableQuery } from 'containers/CDTFATablePage/useCDTFAMainTableQuery'
import { useDebounce } from 'hooks/useDebounce'
import { useEmptyColumnsQuery } from 'hooks/useEmptyColumnsQuery'
import VerticalTable from 'UI/VerticalTable/VerticalTable'
import ExportCSVTableVertical from 'components/ExportCSVTableVertical/ExportCSVTableVertical'
import { COLUMNS_IDS } from 'UI/VerticalTable/utils'
import { dataScienceColumns, keysForEmptyColumnsRequest } from './utils'
import VerticalTableFilters from 'components/VerticalTableFilters/VerticalTableFilters'
import { PAGE_SIZE } from 'components/verticals/constants'
import styles from 'styles/TablePageCommon.scss'
import NoDataContainer from 'components/NoDataContainer'

const CDTFATablePage: FC = () => {
  const currentZone: CurrentZone = useSelector(selectCurrentZone)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const statisticsActivityRef = useRef<HTMLDivElement | null>(null)
  const navigationRef = useRef<HTMLDivElement | null>(null)

  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const { searchValue, searchValueDebounced, sorting, setSearchValue, setSorting } = useVerticalSearchAndSort()

  const [activeFilters, setActiveFilters] = useState<Filter[]>([])
  const [filterAdditionalData, setFilterAdditionalData] = useState<FilterAdditionalData[]>([])

  const activeFiltersDebounced = useDebounce(activeFilters, 500)

  const { data: emptyColumnsData, isFetched: emptyColumnsIsFetched } = useEmptyColumnsQuery({
    verticalIndices: currentZone?.cdtfaIndices,
    currentQueryKey: 'cdtfa-entities-key',
    keys: keysForEmptyColumnsRequest,
  })

  const { data: overviewData, isFetching: isFetchingOverview } = useCDTFAOverviewQuery({
    verticalIndices: currentZone?.cdtfaIndices,
    currentQueryKey: 'cdtfa-overview-key',
    searchValue: searchValueDebounced,
    filters: activeFiltersDebounced,
  })

  const { verticalEntities, fetchNextPage, isFetching, isLoading, isError } =
    useCDTFAMainTableQuery<CDTFAEntityForTable>({
      verticalIndices: currentZone?.cdtfaIndices,
      currentQueryKey: 'cdtfa-entities-key',
      searchValue: searchValueDebounced,
      filters: activeFiltersDebounced,
      sorting,
      pageSize: PAGE_SIZE,
    })

  const { filterOptions } = useFilterOptionsQuery({
    verticalIndices: currentZone?.cdtfaIndices,
    currentQueryKey: 'cdtfa-filter-options-key',
    filtersInitialState,
  })

  useEffect(() => {
    if (overviewData && filterAdditionalData.length === 0) {
      setFilterAdditionalData([
        {
          name: 'aggregatedTotals.qtr_yield_ivix',
          minLimit: overviewData.summary.ivixAdjustedTaxRange.min,
          maxLimit: overviewData.summary.ivixAdjustedTaxRange.max,
        },
      ])
    }
  }, [overviewData, filterAdditionalData])

  const hasNoData = useMemo(() => {
    return !isLoading && !isError && (!verticalEntities || verticalEntities.length === 0) && !searchValueDebounced && activeFiltersDebounced.length === 0
  }, [isLoading, isError, verticalEntities, searchValueDebounced, activeFiltersDebounced])

  return (
    <div ref={containerRef} className={styles.container}>
      <div ref={navigationRef}>
        <nav id='navigationHeaderWrapper' className={styles.navigationHeaderWrapper}>
          <NavigationHeader />
        </nav>
        <header className='page-header withNavigationHeader'>
          <h1 className={cx('page-title', 'h1')}>
            <FormattedMessage {...messages.header} />
          </h1>
        </header>
      </div>
      {hasNoData ? (
        <NoDataContainer verticalName="Data Science" zoneName={currentZone?.name} />
      ) : (
        <>
          <section ref={statisticsActivityRef} className={styles.summary} id={'businessSummary'}>
            <CDTFATablePageOverview
              isLoading={false}
              summary={{
                sitesCount: { ...overviewData?.summary?.sitesCount, total: overviewData?.total?.value || 0 },
                accountsCount: {
                  ...overviewData?.summary?.accounts,
                },
                rawTaxChart: overviewData?.summary?.rawTaxChart,
              }}
            />
          </section>
          {isLoading ? (
            <Flex align='center' gap='middle' justify='center' style={{ padding: '45px 14px' }}>
              <Spin size='large' />
            </Flex>
          ) : isError ? (
            <Alert severity='error' sx={{ fontFamily: 'Source Sans Pro', fontSize: '1rem', alignItems: 'center' }}>
              Something went wrong
            </Alert>
          ) : (
            <>
              {emptyColumnsIsFetched && (
                <VerticalTable
                  pageSize={PAGE_SIZE}
                  enableColumnPicker
                  tableId='cdtfa'
                  dataSource={verticalEntities as any[]}
                  columns={dataScienceColumns}
                  containerRef={containerRef}
                  additionalRefs={[statisticsActivityRef, navigationRef]}
                  fetchNextPage={fetchNextPage}
                  total={overviewData?.total?.value || 0}
                  loading={isFetching}
                  disabledColumns={emptyColumnsData}
                  onSelectionChange={setSelectedIds}
                  removeFromPickerColumns={[COLUMNS_IDS.verifiedName]}
                  rowClassName={styles.tableRow}
                  onRow={record => {
                    return {
                      onClick: () => {
                        window.open(`${window.location.origin}/vertical/data-science/${record.id}`, '_blank')
                      },
                    }
                  }}
                  onChange={(pagination, filters, sorter) => {
                    if (!Array.isArray(sorter)) {
                      if (sorter.order) {
                        setSorting([{ id: sorter.columnKey as string, desc: sorter.order === 'descend' }])
                      } else {
                        setSorting([])
                      }
                    }
                  }}
                >
                  <div className={styles.counter}>
                    {`Data Science: `}
                    <span>{selectedIds.length > 0 ? `${selectedIds.length} selected` : overviewData?.total?.value}</span>
                  </div>
                  <VerticalTableFilters
                    activeFilters={activeFilters}
                    setActiveFilters={setActiveFilters}
                    filtersInitialState={filtersInitialState}
                    filterOptions={filterOptions}
                    filterAdditionalData={filterAdditionalData}
                  />
                  <Search
                    placeholder='input search text'
                    enterButton
                    allowClear
                    style={{ width: 264 }}
                    loading={isFetching}
                    onSearch={newSearchValue => setSearchValue(newSearchValue)}
                  />
                  <ExportCSVTableVertical
                    zoneIndex={currentZone?.cdtfaIndices}
                    csvFileName='Data Science'
                    searchValue={searchValue}
                    selectedIds={selectedIds}
                    sorting={sorting}
                    total={overviewData?.total?.value}
                  />
                </VerticalTable>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

export default CDTFATablePage
